/**
 * What the shop spends.
 *
 * There is no `update` and no `remove`, and their absence is the contract rather
 * than an oversight: an expense is a financial record, corrected by a reversal
 * that keeps both entries. The server refuses PATCH and DELETE; this file does
 * not offer them so nobody writes a screen that expects them.
 */

import { api } from "@/api/client";

export interface ExpenseCategory {
  id: string;
  name: string;
  is_recurring: boolean;
  sort_order: number;
  is_active: boolean;
}

export interface Expense {
  id: string;
  category: string;
  category_name: string;
  incurred_on: string;
  amount: string;
  description: string;
  reference: string;
  supplier: string | null;
  supplier_name: string;
  is_reversal: boolean;
  reverses: string | null;
  recorded_by_name: string | null;
  created_at: string;
}

interface Page<T> {
  count: number;
  next: string | null;
  results: T[];
}

function unwrap<T>(payload: Page<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}

export const expensesRepository = {
  categories: async (): Promise<ExpenseCategory[]> =>
    unwrap(await api.get<Page<ExpenseCategory> | ExpenseCategory[]>("/expense-categories/")),

  list: async (range: { from?: string; to?: string; category?: string } = {}): Promise<Expense[]> => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(range)) {
      if (value) params.set(key, value);
    }
    return unwrap(await api.get<Page<Expense> | Expense[]>(`/expenses/?${params.toString()}`));
  },

  record: (body: {
    category_id: string;
    amount: string;
    description: string;
    incurred_on?: string;
    reference?: string;
  }) => api.post<Expense>("/expenses/", body),

  reverse: (id: string, reason: string) =>
    api.post<Expense>(`/expenses/${id}/reverse/`, { reason }),
};
